/**
 * Workspace shells from the approved mockup:
 * - MerchantShell: 236px sidebar ≥960px, drawer below; org header.
 * - AdminShell: "3PL OPS" sidebar (teal accent) with merchant context.
 * - FloorShell: dark sticky header, single column, mobile-first.
 */
import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Helmet } from 'react-helmet-async';
import {
  ArrowSquareIn, ArrowUUpLeft, Buildings, ChartBar, ChatCircleDots, ClipboardText, Cube, Gauge, Gear, Lifebuoy, List,
  Moon, Package, Plug, Receipt, SignOut, Stack, Sun, Truck, Warning, Warehouse, Wrench, X, UserList,
  ListChecks, ChalkboardTeacher, ArrowSquareOut,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_LOGO_URL } from '../lib/branding';
import { useFl } from './context';
import { Banner, Btn, EmptyState, Loading, Select } from './ui';
import { TRAINING_SITE_URL, TrainingLayer, realSiteHref, useTraining } from './training';

function ThemeButton() {
  const { resolvedTheme, setTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  return (
    <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} aria-label={dark ? 'Light mode' : 'Dark mode'}
      className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-ship-border text-ship-muted hover:text-ship-text focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D96245]">
      {dark ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}

function SideNav({ items, accent, onNavigate }) {
  return (
    <nav aria-label="Workspace" className="flex flex-col gap-0.5">
      {items.map((it) => (it.href ? (
        <a key={it.id} href={it.href} target="_blank" rel="noopener noreferrer" onClick={onNavigate} data-testid={`fl-nav-${it.id}`}
          className="flex h-[42px] items-center gap-3 rounded-[10px] px-3 text-sm font-semibold text-ship-muted transition hover:bg-ship-hover hover:text-ship-text focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D96245]">
          <it.icon size={18} weight="duotone" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{it.label}</span>
          <ArrowSquareOut size={14} aria-hidden />
        </a>
      ) : (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate} data-testid={`fl-nav-${it.id}`}
          className={({ isActive }) => `flex h-[42px] items-center gap-3 rounded-[10px] px-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D96245] ${isActive ? accent.active : 'text-ship-muted hover:bg-ship-hover hover:text-ship-text'}`}>
          <it.icon size={18} weight="duotone" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{it.label}</span>
          {it.badge ? <span className={`rounded-full px-2 text-[11px] font-bold ${accent.badge}`}>{it.badge}</span> : null}
        </NavLink>
      )))}
    </nav>
  );
}

function Frame({ badge, accent, items, header, footer, children, title }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const side = (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4">
      <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="ShipDesi home">
        <img src={DEFAULT_LOGO_URL} alt="" className="h-8 w-8 rounded-lg object-contain" />
        <span className="font-heading text-lg font-bold text-ship-text">ShipDesi</span>
        <span className={`ml-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold tracking-wider ${accent.badge}`}>{badge}</span>
      </Link>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <SideNav items={items} accent={accent} onNavigate={() => setOpen(false)} />
      </div>
      <div className="mt-auto shrink-0">{footer}</div>
    </div>
  );
  return (
    <div data-dashboard className="min-h-screen bg-ship-bg text-ship-text font-body">
      <Helmet><title>{title}</title><meta name="robots" content="noindex" /></Helmet>
      <aside className="fixed inset-y-0 left-0 hidden w-[236px] border-r border-ship-border bg-ship-sidebar min-[960px]:block">
        {side}
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 min-[960px]:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close menu" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[280px] max-w-[86vw] overflow-y-auto bg-ship-sidebar shadow-xl">
            <button type="button" onClick={() => setOpen(false)} aria-label="Close menu"
              className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-lg text-ship-muted"><X size={20} /></button>
            {side}
          </div>
        </div>
      )}
      <div className="min-[960px]:pl-[236px]">
        <header className="sticky top-0 z-30 flex min-h-[60px] items-center gap-3 border-b border-ship-border bg-ship-header/95 px-4 backdrop-blur sm:px-7">
          <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="fl-menu"
            className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-ship-border min-[960px]:hidden"><List size={20} /></button>
          <div className="min-w-0 flex-1">{header}</div>
          <ThemeButton />
        </header>
        <main className="mx-auto w-full max-w-[1280px] px-4 py-5 sm:px-7 sm:py-6"><TrainingLayer />{children}</main>
      </div>
    </div>
  );
}

/** The forwarding console: in the training copy it's the real site's, in a new tab. */
function ForwardingLink({ to, className, testId, children }) {
  const href = realSiteHref(to);
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={`${className} items-center gap-1`} data-testid={testId}>
        {children} <ArrowSquareOut size={12} aria-hidden />
      </a>
    );
  }
  return <Link to={to} className={className} data-testid={testId}>{children}</Link>;
}

function UserFooter({ label }) {
  const { user, logout } = useAuth();
  return (
    <div className="rounded-xl border border-ship-border bg-ship-card p-3 text-xs">
      <div className="truncate font-bold text-ship-text">{user?.name || user?.email}</div>
      <div className="truncate text-ship-subtle">{label}</div>
      <button type="button" onClick={logout} className="mt-2 inline-flex items-center gap-1.5 font-semibold text-ship-muted hover:text-ship-text">
        <SignOut size={14} /> Sign out
      </button>
    </div>
  );
}

// ── Merchant ─────────────────────────────────────────────────────────────────
const MERCHANT_ACCENT = { active: 'bg-[#D96245]/12 text-[#B44B33] dark:text-[#F2B8AA]', badge: 'bg-[#F0D4CC] text-[#8E3A26]' };

export function MerchantShell() {
  const fl = useFl();
  const nav = useNavigate();
  if (fl.me.loading && !fl.me.data) return <div className="p-8"><Loading /></div>;
  if (fl.me.error && !fl.me.data) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <Banner tone="error" title="Couldn’t open your fulfilment workspace" actions={<Btn onClick={fl.me.reload}>Try again</Btn>}>
          {fl.me.errorText}
        </Banner>
      </div>
    );
  }
  if (!fl.merchants.length) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <EmptyState title="This workspace is for fulfilment merchants"
          body="Your login isn’t linked to a fulfilment account yet. If you accepted a quote, open the link from it again while signed in with the same email."
          action={<div className="flex gap-2"><Btn onClick={() => nav('/fulfillment/request-quote')}>Request a quote</Btn><Btn onClick={() => nav('/dashboard')}>Go to my dashboard</Btn></div>} />
      </div>
    );
  }
  const m = fl.merchant;
  const items = [
    { id: 'overview', to: '/merchant', end: true, label: 'Overview', icon: Gauge },
    ...(m.approval !== 'approved' ? [{ id: 'onboarding', to: '/merchant/onboarding', label: 'Onboarding', icon: ListChecks }] : []),
    { id: 'products', to: '/merchant/products', label: 'Products', icon: Cube },
    { id: 'inventory', to: '/merchant/inventory', label: 'Inventory', icon: Stack },
    { id: 'inbound', to: '/merchant/inbound', label: 'Inbound Stock', icon: ArrowSquareIn },
    { id: 'orders', to: '/merchant/orders', label: 'Orders', icon: ClipboardText },
    { id: 'shipments', to: '/merchant/shipments', label: 'Shipments', icon: Truck },
    { id: 'returns', to: '/merchant/returns', label: 'Returns', icon: ArrowUUpLeft },
    ...(fl.can('billing') ? [{ id: 'billing', to: '/merchant/billing', label: 'Billing', icon: Receipt }] : []),
    { id: 'reports', to: '/merchant/reports', label: 'Reports', icon: ChartBar },
    { id: 'connections', to: '/merchant/connections', label: 'Connections', icon: Plug },
    { id: 'support', to: '/merchant/support', label: 'Support', icon: Lifebuoy },
    { id: 'settings', to: '/merchant/settings', label: 'Settings', icon: Gear },
  ];
  const header = (
    <div className="flex min-w-0 items-center gap-3">
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ship-subtle">Your organisation</div>
        {fl.merchants.length > 1 ? (
          <Select value={fl.merchantId} onChange={(e) => fl.chooseMerchant(e.target.value)} aria-label="Organisation"
            className="h-8 w-auto max-w-[260px] py-0 font-semibold">
            {fl.merchants.map((x) => <option key={x.merchant_id} value={x.merchant_id}>{x.name}</option>)}
          </Select>
        ) : (
          <div className="truncate font-heading text-[15px] font-semibold">{m.name} <span className="font-mono text-xs text-ship-subtle">{m.ref}</span></div>
        )}
      </div>
      <ForwardingLink to="/dashboard" className="ml-auto hidden rounded-full border border-ship-border px-3 py-1 text-xs font-semibold text-ship-muted hover:text-ship-text sm:inline-flex"
        testId="fl-switch-forwarding">Forwarding</ForwardingLink>
    </div>
  );
  return (
    <Frame badge="FULFILMENT" accent={MERCHANT_ACCENT} items={items} header={header} title="Fulfilment · ShipDesi"
      footer={<UserFooter label={`${fl.role} · ${m.name}`} />}>
      {m.approval !== 'approved' && (
        <div className="mb-4">
          <Banner tone="warn" title="Onboarding isn’t complete yet"
            actions={<Link className="text-sm font-bold underline" to="/merchant/onboarding">Continue onboarding</Link>}>
            You can add products and send stock. Orders can’t be released until ShipDesi approves your account.
          </Banner>
        </div>
      )}
      <Outlet />
    </Frame>
  );
}

// ── Admin (3PL Ops) ──────────────────────────────────────────────────────────
const ADMIN_ACCENT = { active: 'bg-[#4A7A8F]/14 text-[#3B6475] dark:text-[#9CC3D6]', badge: 'bg-[#EAF1F5] text-[#3B6475]' };
export const ADMIN_CTX_KEY = 'fl.adminMerchantCtx';

export function useAdminContext() {
  const [ctx, setCtx] = useState(() => {
    try { return sessionStorage.getItem(ADMIN_CTX_KEY) || ''; } catch { return ''; }
  });
  useEffect(() => {
    const on = () => { try { setCtx(sessionStorage.getItem(ADMIN_CTX_KEY) || ''); } catch { /* ignore */ } };
    window.addEventListener('fl-admin-ctx', on);
    return () => window.removeEventListener('fl-admin-ctx', on);
  }, []);
  const set = (v) => {
    try { sessionStorage.setItem(ADMIN_CTX_KEY, v || ''); } catch { /* ignore */ }
    setCtx(v || '');
    window.dispatchEvent(new Event('fl-admin-ctx'));
  };
  return [ctx, set];
}

/** The ops sidebar, shared by the ops pages and the floor pages an admin opens from it. */
function adminNavItems(fl, trn) {
  return [
    { id: 'tower', to: '/ops', end: true, label: 'Control Tower', icon: Gauge },
    { id: 'enquiries', to: '/ops/enquiries', label: 'Enquiries & Quotes', icon: UserList },
    { id: 'merchants', to: '/ops/merchants', label: 'Merchants', icon: Buildings },
    { id: 'inbound', to: '/ops/inbound', label: 'Inbound', icon: ArrowSquareIn },
    { id: 'inventory', to: '/ops/inventory', label: 'Inventory', icon: Stack },
    { id: 'orders', to: '/ops/orders', label: 'Orders', icon: ClipboardText },
    { id: 'tasks', to: '/floor', label: 'Warehouse Tasks', icon: Warehouse },
    { id: 'shipments', to: '/ops/shipments', label: 'Shipments', icon: Truck },
    { id: 'returns', to: '/ops/returns', label: 'Returns', icon: ArrowUUpLeft },
    { id: 'exceptions', to: '/ops/exceptions', label: 'Exceptions', icon: Warning },
    { id: 'billing', to: '/ops/billing', label: 'Billing', icon: Receipt },
    { id: 'reports', to: '/ops/reports', label: 'Reports', icon: ChartBar },
    { id: 'config', to: '/ops/config', label: 'Configuration', icon: Wrench },
    ...(trn?.training && fl.staffCan('ops') ? [{ id: 'training', to: '/ops/training', label: 'Training', icon: ChalkboardTeacher }] : []),
    // The real site links out to the training copy (a separate site and database).
    ...(!trn?.training && TRAINING_SITE_URL && fl.staffCan('ops') ? [{ id: 'training-site', href: TRAINING_SITE_URL, label: 'Training copy', icon: ChalkboardTeacher }] : []),
    // 3PL support threads live in the app's shared Messages inbox.
    ...(fl.staffCan('ops') ? [{ id: 'messages', to: '/dashboard?tab=admin-messages', label: 'Messages', icon: ChatCircleDots }] : []),
  ];
}

export function AdminShell({ merchantsForContext = [] }) {
  const fl = useFl();
  const [ctx, setCtx] = useAdminContext();
  const trn = useTraining();
  if (fl.me.loading && !fl.me.data) return <div className="p-8"><Loading /></div>;
  if (!fl.workspaces.admin) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <EmptyState title="Admin access required" body="3PL operations are for ShipDesi staff with an operations or finance role." />
      </div>
    );
  }
  const items = adminNavItems(fl, trn);
  const header = (
    <div className="flex min-w-0 flex-wrap items-center gap-3">
      <ForwardingLink to="/dashboard?tab=admin-dashboard" className="hidden rounded-full border border-ship-border px-3 py-1 text-xs font-semibold text-ship-muted hover:text-ship-text sm:inline-flex">
        Forwarding console
      </ForwardingLink>
      <div className="flex min-w-0 items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-ship-subtle">Merchant context</span>
        <Select value={ctx} onChange={(e) => setCtx(e.target.value)} aria-label="Merchant context" data-testid="fl-admin-ctx"
          className="h-8 w-auto max-w-[220px] py-0 text-[13px] font-semibold">
          <option value="">All merchants</option>
          {merchantsForContext.map((x) => <option key={x.merchant_id} value={x.merchant_id}>{x.name}</option>)}
        </Select>
      </div>
    </div>
  );
  return (
    <Frame badge="3PL OPS" accent={ADMIN_ACCENT} items={items} header={header} title="3PL Ops · ShipDesi"
      footer={<UserFooter label={fl.staffRoles.join(' · ') || 'staff'} />}>
      {ctx && (
        <div className="mb-4">
          <Banner tone="info" actions={<Btn size="sm" onClick={() => setCtx('')}>Show all merchants</Btn>}>
            Showing one merchant: <b>{merchantsForContext.find((x) => x.merchant_id === ctx)?.name || ctx}</b>
          </Banner>
        </div>
      )}
      <Outlet />
    </Frame>
  );
}

// ── Floor ────────────────────────────────────────────────────────────────────
const FloorFramedContext = React.createContext(false);

export function FloorShell() {
  const fl = useFl();
  const trn = useTraining();
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (fl.me.loading && !fl.me.data) return <div className="p-6"><Loading /></div>;
  if (!fl.workspaces.floor) {
    return (
      <div className="mx-auto max-w-md p-6">
        <EmptyState title="Floor access required" body="Warehouse tasks are for ShipDesi operators and supervisors." />
      </div>
    );
  }
  const banner = !online && (
    <div className="mx-auto max-w-[760px] px-3 pt-3">
      <Banner tone="warn" title="You’re offline">
        Nothing is saved on this device. Scans and confirmations will work again when the connection is back.
      </Banner>
    </div>
  );
  // Ops staff arrive from the ops sidebar, so keep it. Floor-only staff keep the lean phone layout.
  if (fl.workspaces.admin) {
    return (
      <FloorFramedContext.Provider value>
        <Frame badge="3PL OPS" accent={ADMIN_ACCENT} items={adminNavItems(fl, trn)} title="Floor · ShipDesi"
          header={(
            <div className="flex items-center gap-3">
              <span className="font-heading font-bold">Warehouse Tasks</span>
              <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${online ? 'bg-[#2E6A49] text-white' : 'bg-[#FFD38A] text-[#1E1B19]'}`}
                data-testid="fl-floor-online">
                {online ? 'Online' : 'Offline — actions need a connection'}
              </span>
            </div>
          )}
          footer={<UserFooter label={fl.staffRoles.join(' · ') || 'staff'} />}>
          {banner}
          <div className="mx-auto w-full max-w-[760px] pb-36"><Outlet /></div>
        </Frame>
      </FloorFramedContext.Provider>
    );
  }
  return (
    <div data-dashboard className="min-h-screen bg-[#EDEAE6] font-body text-[#1E1B19] dark:bg-[#121110] dark:text-[#F5F2EF]">
      <Helmet><title>Floor · ShipDesi</title><meta name="robots" content="noindex" /></Helmet>
      <header className="sticky top-0 z-30 flex items-center gap-3 bg-[#1E1B19] px-3 py-2.5 text-white">
        <Link to="/floor" className="flex items-center gap-2 font-heading font-bold" data-testid="fl-floor-home">
          <Package size={20} weight="duotone" /> ShipDesi · Floor
        </Link>
        <span className={`ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-bold ${online ? 'bg-[#2E6A49] text-white' : 'bg-[#FFD38A] text-[#1E1B19]'}`}
          data-testid="fl-floor-online">
          {online ? 'Online' : 'Offline — actions need a connection'}
        </span>
        <span className="hidden text-xs text-white/70 sm:inline">{fl.me.data?.user?.name}</span>
      </header>
      <TrainingLayer floor />
      {banner}
      <main className="mx-auto w-full max-w-[760px] px-3 pb-36 pt-3"><Outlet /></main>
    </div>
  );
}

/** Fixed bottom action bar for floor screens (56px, thumb-reachable). */
export function FloorActionBar({ children }) {
  const framed = React.useContext(FloorFramedContext);
  return (
    <div className={`fixed inset-x-0 bottom-0 z-30${framed ? ' min-[960px]:left-[236px]' : ''} border-t border-black/10 bg-[#EDEAE6]/95 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 backdrop-blur dark:bg-[#121110]/95`}>
      <div className="mx-auto flex max-w-[760px] gap-2">{children}</div>
    </div>
  );
}

export const ICONS = { Gauge, Truck, Warning, Receipt, Cube };
